(() => {
  const initCountdown = (root) => {
    root.querySelectorAll('[data-pz-countdown]').forEach((el) => {
      if (el.dataset.pzCountdownReady === 'true') return;

      el.dataset.pzCountdownReady = 'true';

      const targetRaw = el.getAttribute('data-pz-countdown');
      const target = Date.parse(targetRaw || '');

      if (!Number.isFinite(target)) {
        el.hidden = true;
        return;
      }

      const update = () => {
        const diff = target - Date.now();

        if (diff <= 0) {
          el.querySelectorAll('[data-pz-time]').forEach((node) => {
            node.textContent = '00';
          });

          el.classList.add('is-complete');
          return;
        }

        const day = Math.floor(diff / 86400000);
        const hour = Math.floor((diff % 86400000) / 3600000);
        const minute = Math.floor((diff % 3600000) / 60000);
        const second = Math.floor((diff % 60000) / 1000);

        const values = {
          days: day,
          hours: hour,
          minutes: minute,
          seconds: second
        };

        Object.entries(values).forEach(([key, value]) => {
          const node = el.querySelector(`[data-pz-time="${key}"]`);

          if (node) {
            node.textContent = String(value).padStart(2, '0');
          }
        });
      };

      update();

      const timer = window.setInterval(update, 1000);
      el._pzTimer = timer;
    });
  };

  const initReveal = (root) => {
    const nodes = root.querySelectorAll('.pz-reveal, .pz-stagger');

    if (!('IntersectionObserver' in window)) {
      nodes.forEach((node) => node.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    nodes.forEach((node) => {
      if (node.dataset.pzRevealReady === 'true') return;

      node.dataset.pzRevealReady = 'true';
      observer.observe(node);
    });
  };

  const initPointer = (root) => {
    root.querySelectorAll('[data-pz-pointer]').forEach((el) => {
      if (el.dataset.pzPointerReady === 'true') return;

      el.dataset.pzPointerReady = 'true';

      el.addEventListener(
        'pointermove',
        (event) => {
          const rect = el.getBoundingClientRect();

          const x = ((event.clientX - rect.left) / rect.width) * 100;
          const y = ((event.clientY - rect.top) / rect.height) * 100;

          el.style.setProperty('--pz-x', `${x}%`);
          el.style.setProperty('--pz-y', `${y}%`);
        },
        { passive: true }
      );
    });
  };

  const initParallax = (root) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    root.querySelectorAll('[data-pz-parallax]').forEach((wrap) => {
      if (wrap.dataset.pzParallaxReady === 'true') return;

      wrap.dataset.pzParallaxReady = 'true';

      const items = wrap.querySelectorAll('[data-pz-depth]');
      let frame = null;

      const move = (event) => {
        if (frame) {
          cancelAnimationFrame(frame);
        }

        frame = requestAnimationFrame(() => {
          const rect = wrap.getBoundingClientRect();

          const nx =
            ((event.clientX - rect.left) / rect.width - 0.5) * 2;

          const ny =
            ((event.clientY - rect.top) / rect.height - 0.5) * 2;

          items.forEach((item) => {
            const depth = Number(
              item.getAttribute('data-pz-depth') || 0.2
            );

            item.style.setProperty(
              '--pz-parallax-x',
              `${nx * depth * 28}px`
            );

            item.style.setProperty(
              '--pz-parallax-y',
              `${ny * depth * 22}px`
            );
          });
        });
      };

      const reset = () => {
        items.forEach((item) => {
          item.style.setProperty('--pz-parallax-x', '0px');
          item.style.setProperty('--pz-parallax-y', '0px');
        });
      };

      wrap.addEventListener('pointermove', move, {
        passive: true
      });

      wrap.addEventListener('pointerleave', reset, {
        passive: true
      });
    });
  };

  const initRails = (root) => {
    root.querySelectorAll('[data-pz-rail-wrap]').forEach((wrap) => {
      if (wrap.dataset.pzRailReady === 'true') return;

      wrap.dataset.pzRailReady = 'true';

      const rail = wrap.querySelector('[data-pz-rail]');

      if (!rail) return;

      const previous = wrap.querySelector('[data-pz-prev]');
      const next = wrap.querySelector('[data-pz-next]');

      const step = () => Math.max(rail.clientWidth * 0.78, 280);

      previous?.addEventListener('click', () => {
        rail.scrollBy({
          left: -step(),
          behavior: 'smooth'
        });
      });

      next?.addEventListener('click', () => {
        rail.scrollBy({
          left: step(),
          behavior: 'smooth'
        });
      });

      let dragging = false;
      let startX = 0;
      let scrollStart = 0;

      rail.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) {
          return;
        }

        dragging = true;
        rail.classList.add('is-dragging');

        startX = event.clientX;
        scrollStart = rail.scrollLeft;

        rail.setPointerCapture?.(event.pointerId);
      });

      rail.addEventListener('pointermove', (event) => {
        if (!dragging) return;

        rail.scrollLeft =
          scrollStart - (event.clientX - startX) * 1.15;
      });

      const end = (event) => {
        if (!dragging) return;

        dragging = false;
        rail.classList.remove('is-dragging');

        rail.releasePointerCapture?.(event.pointerId);
      };

      rail.addEventListener('pointerup', end);
      rail.addEventListener('pointercancel', end);

      rail.addEventListener('pointerleave', (event) => {
        if (dragging && event.pointerType === 'mouse') {
          end(event);
        }
      });
    });
  };

  const initTilt = (root) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    if (window.matchMedia('(hover: none)').matches) {
      return;
    }

    root.querySelectorAll('[data-pz-tilt]').forEach((card) => {
      if (card.dataset.pzTiltReady === 'true') return;

      card.dataset.pzTiltReady = 'true';

      let frame = null;

      card.addEventListener(
        'pointermove',
        (event) => {
          if (frame) {
            cancelAnimationFrame(frame);
          }

          frame = requestAnimationFrame(() => {
            const rect = card.getBoundingClientRect();

            const x = (event.clientX - rect.left) / rect.width;
            const y = (event.clientY - rect.top) / rect.height;

            const rotateY = (x - 0.5) * 8;
            const rotateX = (0.5 - y) * 7;

            card.style.transform =
              `perspective(900px) ` +
              `rotateX(${rotateX}deg) ` +
              `rotateY(${rotateY}deg) ` +
              `translateY(-3px)`;
          });
        },
        { passive: true }
      );

      card.addEventListener(
        'pointerleave',
        () => {
          card.style.transform = '';
        },
        { passive: true }
      );
    });
  };

  const initQuickAdd = (root) => {
    root.querySelectorAll('[data-pz-quick-add]').forEach((form) => {
      if (form.dataset.pzQuickReady === 'true') return;

      form.dataset.pzQuickReady = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const button = form.querySelector(
          'button[type="submit"]'
        );

        const original = button?.innerHTML;

        if (button) {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        }

        try {
          const shopRoot =
            window.Shopify?.routes?.root || '/';

          const response = await fetch(
            `${shopRoot}cart/add.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
              },
              body: JSON.stringify({
                items: [
                  {
                    id: Number(form.dataset.variantId),
                    quantity: 1
                  }
                ]
              })
            }
          );

          if (!response.ok) {
            throw new Error(
              'No se pudo añadir al carrito'
            );
          }

          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              bubbles: true
            })
          );

          document.dispatchEvent(
            new CustomEvent('cart:open', {
              bubbles: true
            })
          );

          if (button) {
            button.innerHTML = '✓';
          }

          window.setTimeout(() => {
            if (button) {
              button.innerHTML = original;
            }
          }, 1300);
        } catch (error) {
          console.error('[Pokezilla] Quick add error:', error);

          if (button) {
            button.innerHTML = '!';
          }

          window.setTimeout(() => {
            if (button) {
              button.innerHTML = original;
            }
          }, 1300);
        } finally {
          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }
        }
      });
    });
  };

  const init = (root = document) => {
    initReveal(root);
    initPointer(root);
    initParallax(root);
    initRails(root);
    initTilt(root);
    initCountdown(root);
    initQuickAdd(root);
  };

  if (!window.PZBrand) {
    window.PZBrand = {
      init
    };

    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => init(document),
        { once: true }
      );
    } else {
      init(document);
    }

    document.addEventListener(
      'shopify:section:load',
      (event) => init(event.target)
    );
  } else {
    window.PZBrand.init(document);
  }
})();